import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { type ReportingPeriod, resolveReportingPeriod } from "@/stores/reporting-period";
import { precedingAdGroupsPeriod } from "../ad-groups/campaign-ad-groups-query";
import { CampaignTargetsTable } from "./CampaignTargetsTable";
import { campaignTargetsQuery } from "./campaign-targets-query";

interface CampaignTargetsProps {
  campaignId: string;
  range: ReportingPeriod;
  timeZone: ReportingTimeZone;
  renderAdGroupLink?: (adGroupId: string, name: string) => ReactNode;
}

/** Campaign-level 投放 tab: every positive target across the campaign's ad groups. */
export function CampaignTargets({
  campaignId,
  range,
  timeZone,
  renderAdGroupLink,
}: CampaignTargetsProps) {
  const [comparison, setComparison] = useState(false);
  const dates = resolveReportingPeriod(range, new Date(), timeZone);
  const query = useQuery(campaignTargetsQuery(campaignId, { type: "custom", ...dates }, timeZone));
  const previous = useQuery({
    ...campaignTargetsQuery(campaignId, precedingAdGroupsPeriod(dates), timeZone),
    enabled: comparison,
  });
  return (
    <CampaignTargetsTable
      campaignId={campaignId}
      data={query.data}
      previousData={comparison ? previous.data : undefined}
      isLoading={query.isPending}
      isFetching={query.isFetching}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      comparison={comparison}
      onComparisonChange={setComparison}
      comparisonLoading={comparison && previous.isPending}
      comparisonError={comparison && previous.isError}
      onComparisonRetry={() => void previous.refetch()}
      renderAdGroupLink={renderAdGroupLink}
    />
  );
}
